/**
 * Admin staging — binary placement drill-down (matches user PWA 安置树 picker).
 */
import { useCallback, useState, type CSSProperties } from 'react';
import { Button, Input, Radio, Space, Spin, Typography, message } from 'antd';
import { RightOutlined, SearchOutlined } from '@ant-design/icons';
import { api, ApiError } from '../../api/client';

export type PlacementStepNode = {
  id: number;
  username: string;
  level_code: string;
  position?: string | null;
  has_left: boolean;
  has_right: boolean;
  left?: { id: number; username: string; level_code: string } | null;
  right?: { id: number; username: string; level_code: string } | null;
};

type Breadcrumb = { id: number; username: string };

type Props = {
  disabled?: boolean;
  running?: boolean;
  onRegister: (params: {
    placement_user_id: number;
    placement_username: string;
    position: 'L' | 'R';
    sponsor_username: string;
    star_package: 'E1' | 'E2';
  }) => void;
};

const cardStyle: CSSProperties = {
  borderRadius: 12,
  padding: '16px 18px',
  background: 'linear-gradient(135deg, #1a2332 0%, #243044 100%)',
  border: '1px solid rgba(212, 175, 55, 0.25)',
  color: '#e8edf5',
};

export function StagingPlacementPicker({ disabled, running, onRegister }: Props) {
  const [rootInput, setRootInput] = useState('');
  const [sponsorInput, setSponsorInput] = useState('');
  const [starPackage, setStarPackage] = useState<'E1' | 'E2'>('E1');
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<Breadcrumb[]>([]);
  const [step, setStep] = useState<PlacementStepNode | null>(null);

  const loadStep = useCallback(async (userId: number) => {
    setStepLoading(true);
    try {
      const data = await api.get<PlacementStepNode>(
        '/api/admin/staging-reward-test/placement/step',
        { user_id: userId },
      );
      setStep(data);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '加载节点失败');
      setStep(null);
    } finally {
      setStepLoading(false);
    }
  }, []);

  async function searchRoot() {
    const username = rootInput.trim();
    if (!username) {
      message.warning('请输入起始用户名');
      return;
    }
    setLoading(true);
    try {
      const user = await api.get<{ id: number; username: string }>(
        '/api/admin/staging-reward-test/placement/lookup',
        { username },
      );
      setCurrentUserId(user.id);
      setBreadcrumb([{ id: user.id, username: user.username }]);
      if (!sponsorInput.trim()) setSponsorInput(user.username);
      await loadStep(user.id);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '用户未找到');
    } finally {
      setLoading(false);
    }
  }

  function drillTo(child: { id: number; username: string }) {
    const next = [...breadcrumb, { id: child.id, username: child.username }];
    setBreadcrumb(next);
    setCurrentUserId(child.id);
    void loadStep(child.id);
  }

  function goToCrumb(index: number) {
    const next = breadcrumb.slice(0, index + 1);
    const target = next[next.length - 1]!;
    setBreadcrumb(next);
    setCurrentUserId(target.id);
    void loadStep(target.id);
  }

  function handleRegister(position: 'L' | 'R') {
    if (!step) return;
    const sponsor = sponsorInput.trim();
    if (!sponsor) {
      message.warning('请输入赞助账号');
      return;
    }
    onRegister({
      placement_user_id: step.id,
      placement_username: step.username,
      position,
      sponsor_username: sponsor,
      star_package: starPackage,
    });
  }

  return (
    <div style={{ maxWidth: 480 }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        输入用户名进入网体，点击旗下成员逐层往下；到达目标节点后点「左区/右区注册」执行测试并查看奖励。
      </Typography.Paragraph>

      <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
        <Input
          placeholder="起始用户名（网体入口）"
          value={rootInput}
          onChange={(e) => setRootInput(e.target.value)}
          onPressEnter={() => void searchRoot()}
          disabled={disabled || loading}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          loading={loading}
          disabled={disabled}
          onClick={() => void searchRoot()}
        >
          进入网体
        </Button>
      </Space.Compact>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          placeholder="赞助账号"
          value={sponsorInput}
          onChange={(e) => setSponsorInput(e.target.value)}
          style={{ width: 180 }}
          disabled={disabled}
        />
        <Radio.Group
          value={starPackage}
          onChange={(e) => setStarPackage(e.target.value as 'E1' | 'E2')}
          disabled={disabled}
        >
          <Radio.Button value="E1">E1</Radio.Button>
          <Radio.Button value="E2">E2</Radio.Button>
        </Radio.Group>
      </Space>

      {breadcrumb.length > 0 && (
        <div style={{ marginBottom: 10, fontSize: 12, lineHeight: 1.8 }}>
          {breadcrumb.map((crumb, index) => (
            <span key={crumb.id}>
              {index > 0 && <span style={{ color: '#999', margin: '0 4px' }}>›</span>}
              <button
                type="button"
                onClick={() => goToCrumb(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: index === breadcrumb.length - 1 ? '#d4af37' : '#1677ff',
                  fontWeight: index === breadcrumb.length - 1 ? 600 : 400,
                }}
              >
                {crumb.username}
              </button>
            </span>
          ))}
        </div>
      )}

      {stepLoading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}

      {!stepLoading && step && (
        <div style={cardStyle}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#8a96a8', marginBottom: 4 }}>当前节点</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#f0d78c' }}>{step.username}</div>
            <div style={{ fontSize: 11, color: '#8a96a8', fontFamily: 'monospace', marginTop: 2 }}>
              {step.level_code}
              {step.position ? ` · ${step.position}` : ''}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
            <Button
              size="small"
              loading={running}
              disabled={disabled || running}
              onClick={() => handleRegister('L')}
              style={{
                height: 36,
                borderColor: 'rgba(212, 175, 55, 0.5)',
                background: 'rgba(212, 175, 55, 0.12)',
                color: '#f0d78c',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {step.has_left ? '左区注册（滑落）' : '+ 左区注册'}
            </Button>
            <Button
              size="small"
              loading={running}
              disabled={disabled || running || !step.has_left}
              onClick={() => handleRegister('R')}
              style={{
                height: 36,
                borderColor: 'rgba(212, 175, 55, 0.5)',
                background: 'rgba(212, 175, 55, 0.12)',
                color: '#f0d78c',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {!step.has_left
                ? '请先落左区 2.1'
                : step.has_right
                  ? '右区注册（滑落）'
                  : '+ 右区注册'}
            </Button>
          </div>

          {(step.left || step.right) && (
            <>
              <div style={{ height: 1, background: 'rgba(212, 175, 55, 0.15)', marginBottom: 10 }} />
              <div style={{ fontSize: 11, color: '#8a96a8', marginBottom: 8 }}>
                旗下成员 — 点击进入下一层
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {step.left && (
                  <button
                    type="button"
                    onClick={() => drillTo(step.left!)}
                    disabled={disabled || running}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.08)',
                      background: 'rgba(0,0,0,0.2)',
                      color: '#e8edf5',
                      cursor: 'pointer',
                      fontSize: 13,
                      textAlign: 'left',
                    }}
                  >
                    <span>
                      <span style={{ color: '#6b9e78', marginRight: 6 }}>L</span>
                      · {step.left.username}
                    </span>
                    <RightOutlined style={{ color: '#666', fontSize: 11 }} />
                  </button>
                )}
                {step.right && (
                  <button
                    type="button"
                    onClick={() => drillTo(step.right!)}
                    disabled={disabled || running}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.08)',
                      background: 'rgba(0,0,0,0.2)',
                      color: '#e8edf5',
                      cursor: 'pointer',
                      fontSize: 13,
                      textAlign: 'left',
                    }}
                  >
                    <span>
                      <span style={{ color: '#c97a7a', marginRight: 6 }}>R</span>
                      · {step.right.username}
                    </span>
                    <RightOutlined style={{ color: '#666', fontSize: 11 }} />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!stepLoading && !step && breadcrumb.length === 0 && (
        <div
          style={{
            ...cardStyle,
            textAlign: 'center',
            color: '#8a96a8',
            fontSize: 13,
            padding: 24,
          }}
        >
          请先在上方输入用户名进入网体
        </div>
      )}
    </div>
  );
}
